import { useState } from "react";
import { Button, DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getExpiringSoon } from "../../api/reportsApi";
import type { ExpiringSoonItemOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { WriteOffModal } from "../../components/WriteOffModal";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

/**
 * `GET /reports/expiring-soon?days=&branch_id=` (§10.5) — what makes expiry loss
 * preventable instead of discovered too late. Each row has a real write-off action
 * (item 6) rather than requiring a separate trip to the item detail page.
 */
export function ExpiringSoonPage() {
  const { ensureFreshToken } = useAuth();
  const [days, setDays] = useState(30);
  const [branchId, setBranchId] = useState("");
  const [writeOffTarget, setWriteOffTarget] = useState<ExpiringSoonItemOut | null>(null);

  const state = useAsyncData(
    () => ensureFreshToken().then((token) => getExpiringSoon(token, days, branchId || undefined)),
    [days, branchId],
  );

  const columns: DataTableColumn<ExpiringSoonItemOut, keyof ExpiringSoonItemOut>[] = [
    { key: "sku", header: "SKU", render: (r) => r.sku },
    { key: "name", header: "Name", render: (r) => r.name },
    { key: "batch_number", header: "Batch", render: (r) => r.batch_number ?? "—" },
    { key: "expiry_date", header: "Expiry", render: (r) => r.expiry_date },
    {
      key: "days_until_expiry",
      header: "Days left",
      align: "right",
      render: (r) => (
        <span className={`badge ${r.days_until_expiry <= 7 ? "badge--overdue" : r.days_until_expiry <= 14 ? "badge--due-soon" : "badge--current"}`}>
          {r.days_until_expiry}
        </span>
      ),
    },
    { key: "quantity_remaining", header: "This batch", align: "right", render: (r) => r.quantity_remaining },
    {
      key: "id",
      header: "",
      render: (r) => (
        <Button variant="danger" size="md" type="button" onClick={() => setWriteOffTarget(r)}>
          Write off
        </Button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Expiring Soon</h1>
          <p className="page__subtitle">Batches approaching expiry — catch loss before it happens, not after.</p>
        </div>
      </div>

      {state.error ? <StatusBanner kind="error">{state.error}</StatusBanner> : null}

      <div className="filter-bar">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="expiring-days">
            Within (days)
          </label>
          <input
            id="expiring-days"
            className="sl-input"
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(Number(e.target.value) || 30)}
          />
        </div>
        <BranchSelect id="expiring-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
      </div>

      <div className="panel">
        {state.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={columns} rows={state.data ?? []} getRowId={(row) => row.id} emptyMessage="Nothing expiring in this window." caption="Expiring Soon" />
        )}
      </div>

      {writeOffTarget ? (
        <WriteOffModal
          open
          onClose={() => setWriteOffTarget(null)}
          itemId={writeOffTarget.inventory_item_id}
          itemName={writeOffTarget.name}
          currentStock={writeOffTarget.quantity_remaining}
          initialBatchId={writeOffTarget.id}
          onWrittenOff={state.reload}
        />
      ) : null}
    </div>
  );
}
